import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PasswordField from '../components/PasswordField';
import { RendPlexLogo } from '../components/RendPlexLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import DarkModeToggle from '../components/DarkModeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Google auth is hidden until the OAuth allowlist is verified on the production
// custom domain (Pre-Cohort Stage 1A — PCR-AUTH-001). Set this env flag to
// "true" to re-expose the button once Google is fully configured.
const GOOGLE_AUTH_ENABLED = (process.env.REACT_APP_GOOGLE_AUTH_ENABLED || '').toLowerCase() === 'true';

function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL — this is Emergent-managed OAuth.
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/login`, { email, password }, { withCredentials: true });
      navigate('/dashboard');
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || t('auth.errors.generic');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14] flex flex-col">
      <nav className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0B0E14]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 flex justify-between items-center">
          <Link to="/"><RendPlexLogo size="md" /></Link>
          <div className="flex items-center gap-3">
            <DarkModeToggle />
            <LanguageSwitcher variant="minimal" />
          </div>
        </div>
      </nav>
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-8">
          <h1 data-testid="login-title" className="font-primary font-bold text-2xl md:text-3xl text-slate-900 dark:text-white mb-2">
            {t('auth.login.title')}
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">{t('auth.login.subtitle')}</p>

          {GOOGLE_AUTH_ENABLED && (
            <>
              <Button data-testid="login-google-btn" onClick={handleGoogle} variant="outline" className="w-full mb-4 rounded-xl">
                {t('auth.continueWithGoogle')}
              </Button>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200 dark:border-slate-700" /></div>
                <div className="relative flex justify-center text-xs uppercase tracking-widest">
                  <span className="bg-white dark:bg-[#131824] px-3 text-slate-400">{t('auth.or')}</span>
                </div>
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input id="email" data-testid="login-email" type="email" autoComplete="email" required
                value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="password">{t('auth.password')}</Label>
              <PasswordField
                id="password"
                data-testid="login-password"
                testIdToggle="login-password-toggle"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1"
              />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={submitting}
              className="w-full bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white rounded-xl">
              {submitting ? t('auth.login.submitting') : t('auth.login.submit')}
            </Button>
          </form>

          <div className="mt-6 flex flex-col items-center gap-2 text-sm">
            <Link data-testid="login-forgot-link" to="/forgot-password" className="text-[#45C04A] underline">{t('auth.login.forgotLink')}</Link>
            <p className="text-slate-600 dark:text-slate-400">
              {t('auth.login.noAccount')}{' '}
              <Link data-testid="login-signup-link" to="/signup" className="text-[#45C04A] underline">{t('auth.login.createAccount')}</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
